import React, { useState, useRef, useEffect } from "react";
import "./CustomSelect.css";

/**
 * Custom Select Component - Modern dropdown with custom styling
 * Ported from sophyapp_therapist (single-select only).
 * @param {string} value - Currently selected value
 * @param {function} onChange - Callback when value changes (receives value string)
 * @param {Array} options - Array of { value: string, label: string }
 * @param {string} placeholder - Placeholder text when no value selected
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Whether the select is disabled
 * @param {string} size - Size variant: 'sm', 'md' (default), 'lg'
 */
export default function CustomSelect({
  value,
  onChange,
  options = [],
  placeholder = "",
  className = "",
  disabled = false,
  size = "md",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isOpen]);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsOpen(!isOpen);
    } else if (event.key === "ArrowDown" && isOpen) {
      event.preventDefault();
      const currentIndex = options.findIndex((opt) => opt.value === value);
      const nextIndex = Math.min(currentIndex + 1, options.length - 1);
      onChange(options[nextIndex].value);
    } else if (event.key === "ArrowUp" && isOpen) {
      event.preventDefault();
      const currentIndex = options.findIndex((opt) => opt.value === value);
      const prevIndex = Math.max(currentIndex - 1, 0);
      onChange(options[prevIndex].value);
    }
  };

  const selectedOption = options.find((opt) => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : placeholder;

  const sizeClass = size !== "md" ? `sophy-select-${size}` : "";

  return (
    <div
      ref={containerRef}
      className={`sophy-select ${sizeClass} ${className} ${isOpen ? "open" : ""} ${disabled ? "disabled" : ""}`}
      role="listbox"
      aria-expanded={isOpen}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={handleKeyDown}
    >
      <div
        className="sophy-select-trigger"
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span
          className={`sophy-select-value ${!selectedOption ? "sophy-select-placeholder" : ""}`}
        >
          {displayValue}
        </span>
        <span className="sophy-select-arrow">
          <svg width="12" height="8" viewBox="0 0 12 8" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M1 1l5 5 5-5"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>

      {isOpen && (
        <div className="sophy-select-options">
          {options.map((option) => (
            <div
              key={option.value}
              className={`sophy-select-option ${option.value === value ? "selected" : ""}`}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
